import { useEffect, useState } from "react";
import { useOrder } from "../../context/OrderContext";
import api from "../../lib/api";
import { toast } from "sonner";
import errorMessage from "../../lib/errors";
import useMeasurementsAutosave from "../../lib/useMeasurementsAutosave";
import MeasurementForm from "../MeasurementForm";
import MeasurementGuide from "../MeasurementGuide";
import MeasurementConfirmModal from "../MeasurementConfirmModal";
import AutosaveBadge from "../AutosaveBadge";
import { Loader2, Mail } from "lucide-react";

export default function StepMeasurements() {
  const { state, update, goNext } = useOrder();
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const items = state.items;
  const isWindow = state.product?.type === "window";

  const valid = items.length === state.quantity && items.every(
    (it) => Number(it.width) > 0 && Number(it.height) > 0 && it.frame_color
      && (isWindow || it.handle_side === "left" || it.handle_side === "right")
  );

  const setItems = (next) => update({ items: next });

  // One-time hydrate from server draft (covers customer who started on phone, finished on laptop).
  useEffect(() => {
    if (!state.orderId || !state.measurementToken) return;
    let active = true;
    (async () => {
      try {
        const r = await api.get(
          `/orders/${state.orderId}/measurement-access?token=${encodeURIComponent(state.measurementToken)}`,
        );
        const draft = r.data?.measurements_draft?.items;
        if (active && Array.isArray(draft) && draft.length === state.quantity) {
          // Only hydrate fields that the customer actually started filling — avoid clobbering the
          // empty defaults with `null` if the draft is older than the local state.
          const merged = items.map((local, i) => {
            const d = draft[i] || {};
            const has = (v) => v !== null && v !== undefined && v !== "";
            return {
              ...local,
              width: has(d.width) ? String(d.width) : local.width,
              height: has(d.height) ? String(d.height) : local.height,
              frame_color: has(d.frame_color) ? d.frame_color : local.frame_color,
              notes: has(d.notes) ? d.notes : local.notes,
              photo_path: has(d.photo_path) ? d.photo_path : local.photo_path,
              photo_url: has(d.photo_url) ? d.photo_url : local.photo_url,
              spring_side: has(d.spring_side) ? d.spring_side : local.spring_side,
              handle_side: has(d.handle_side) ? d.handle_side : local.handle_side,
              has_springs: typeof d.has_springs === "boolean" ? d.has_springs : (local.has_springs !== false),
              has_pull_tabs: typeof d.has_pull_tabs === "boolean" ? d.has_pull_tabs : (local.has_pull_tabs !== false),
              petscreen: typeof d.petscreen === "boolean" ? d.petscreen : local.petscreen,
            };
          });
          setItems(merged);
        }
      } catch {
        /* ignore — proceed with localStorage state */
      }
    })();
    return () => { active = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.orderId, state.measurementToken]);

  const { saving: autosaving, savedAt } = useMeasurementsAutosave({
    orderId: state.orderId,
    token: state.measurementToken,
    items,
    enabled: !!state.orderId && !!state.measurementToken,
  });

  const submit = async () => {
    if (!valid || saving) return;
    setSaving(true);
    try {
      await api.post(`/orders/${state.orderId}/measurements`, {
        items: items.map((it) => ({
          width: Number(it.width),
          height: Number(it.height),
          unit: "in",
          frame_color: it.frame_color,
          notes: it.notes || "",
          photo_path: it.photo_path || null,
          petscreen: !!it.petscreen,
          spring_side: it.spring_side || null,
          has_springs: it.has_springs !== false,
          has_pull_tabs: it.has_pull_tabs !== false,
          handle_side: it.handle_side || null,
        })),
      });
      setConfirmOpen(false);
      goNext();
      toast.success("Measurements saved");
    } catch (e) {
      toast.error(errorMessage(e, "Failed to save"));
    } finally {
      setSaving(false);
    }
  };

  const requestSubmit = () => {
    if (!valid || saving) return;
    setConfirmOpen(true);
  };

  const skip = () => goNext();

  // Customer chose Pro Measurement from the in-form guide. Add it to upsells, skip the form,
  // and advance to Done — the shop will send a tech to take measurements.
  const addProMeasurement = async () => {
    try {
      const cur = (await api.get(`/orders/${state.orderId}`)).data;
      const nextUpsells = { ...(cur.upsells || {}), pro_measurement: true };
      await api.post(`/orders/${state.orderId}/upsells`, { upsells: nextUpsells });
      update({ upsells: { ...state.upsells, pro_measurement: true }, measurementsRequired: false });
      toast.success("Pro Measurement added — we'll be in touch to schedule.");
      goNext();
    } catch (e) {
      toast.error(errorMessage(e, "Couldn't add Pro Measurement"));
    }
  };

  return (
    <div data-testid="step-measurements">
      <div className="step-pill mb-3" style={{ background: "#dcfce7", color: "#166534" }}>
        Order paid · {state.orderNumber}
      </div>
      <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-2 text-[var(--brand-text)]">
        Now, your measurements
      </h1>
      <p className="muted-text mb-4">
        Enter the dimensions for each {state.product?.name?.toLowerCase()}.
        Don&apos;t have them handy? Skip — we&apos;ll send a link to do this later.
      </p>

      <div className="rounded-2xl p-3 mb-3 border" style={{ background: "#f0fdf4", borderColor: "#bbf7d0" }}>
        <p className="text-xs leading-snug text-green-900">
          <Mail className="w-3.5 h-3.5 inline mr-1" />
          We just emailed and texted you a link in case you want to enter
          measurements later from your phone.
        </p>
      </div>

      <AutosaveBadge saving={autosaving} savedAt={savedAt} />

      <MeasurementGuide
        productType={isWindow ? "window" : "sliding"}
        onAddProMeasurement={addProMeasurement}
      />

      <MeasurementForm
        items={items}
        onChange={setItems}
        petscreenAll={state.petscreenAll}
        showSpringAxis={isWindow}
      />

      <div className="flex gap-3 mt-8">
        <button data-testid="skip-measurements-btn" type="button" onClick={skip} disabled={saving}
          className="flex-1 py-3 rounded-full border-2 border-[var(--brand-border)] font-semibold">
          I&apos;ll do it later
        </button>
        <button data-testid="submit-measurements-btn" className="cta-btn flex-1"
          disabled={!valid || saving} onClick={requestSubmit}>
          {saving
            ? <span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Saving</span>
            : "Submit measurements"}
        </button>
      </div>

      <MeasurementConfirmModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={submit}
        items={items}
        productName={state.product?.name || "screen"}
        saving={saving}
      />
    </div>
  );
}
